import { SiteHeader } from "../components/layout/SiteHeader";
import { SiteFooter } from "../components/layout/SiteFooter";

export function PrivacyPage() {
  return (
    <div className="min-h-screen flex flex-col bg-surface-950">
      <SiteHeader />
      <div className="flex-1">
      {/* Page Title */}
      <div className="bg-surface-900/80 border-b border-surface-800/50 py-10">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-3xl font-bold text-surface-100">Privacy Policy</h1>
          <p className="text-surface-400 mt-2">Effective Date: January 31, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-invert prose-surface max-w-none space-y-8">
          <section>
            <p className="text-surface-300 leading-relaxed">
              Your privacy matters to us. This Privacy Policy explains how Colvix LLC ("we," "us," or "our") collects, uses, and safeguards your information when you use TradeCraft at tradecraft.colvix.net (the "Platform"). TradeCraft is a quantitative investment research platform designed to help investors build and analyze trading strategies. By using our Platform, you agree to the practices outlined in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-surface-100 mb-4">Information We Collect</h2>
            <p className="text-surface-300 leading-relaxed">
              To provide our services, we may collect personally identifiable information that you voluntarily provide, including:
            </p>
            <ul className="list-disc list-inside text-surface-300 mt-3 space-y-1">
              <li>Your name</li>
              <li>Email address</li>
              <li>Account credentials</li>
            </ul>
            <p className="text-surface-300 leading-relaxed mt-4">
              We only collect personal information when you choose to provide it—for example, when creating an account, contacting support, subscribing to updates, or using features that require authentication. We may collect additional information in the future as our services evolve.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-surface-100 mb-4">How We Use Your Information</h2>
            <p className="text-surface-300 leading-relaxed">
              Colvix LLC uses the information we collect to:
            </p>
            <ul className="list-disc list-inside text-surface-300 mt-3 space-y-1">
              <li>Operate and deliver the services you request</li>
              <li>Communicate with you about your account and our products</li>
              <li>Send important updates, security alerts, and service notifications</li>
              <li>Improve our Platform and develop new features</li>
              <li>Inform you of other products or services that may interest you</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-surface-100 mb-4">Information Sharing</h2>
            <p className="text-surface-300 leading-relaxed">
              We do not sell, rent, or lease your personal information to third parties.
            </p>
            <p className="text-surface-300 leading-relaxed mt-4">
              We may share data with trusted service providers who assist us with operations such as email delivery, analytics, customer support, or infrastructure hosting. These partners are contractually obligated to use your information only to provide services on our behalf and must maintain its confidentiality.
            </p>
            <p className="text-surface-300 leading-relaxed mt-4">
              We may also disclose your information without prior notice if required by law, or when we believe disclosure is necessary to: (a) comply with legal processes or government requests; (b) protect our rights, property, or safety; or (c) respond to emergencies affecting user safety or public welfare.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-surface-100 mb-4">Automatically Collected Data</h2>
            <p className="text-surface-300 leading-relaxed">
              When you visit our Platform, we may automatically collect technical information including your IP address, browser type, operating system, access times, pages viewed, and referring URLs. This data helps us maintain service quality, analyze usage patterns, and improve the user experience.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-surface-100 mb-4">External Links</h2>
            <p className="text-surface-300 leading-relaxed">
              Our Platform may contain links to third-party websites or services. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any site you visit after leaving our Platform.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-surface-100 mb-4">Data Security</h2>
            <p className="text-surface-300 leading-relaxed">
              We implement reasonable security measures to protect your personal information from unauthorized access, alteration, or disclosure. Sensitive data transmitted to our servers is encrypted using industry-standard protocols.
            </p>
            <p className="text-surface-300 leading-relaxed mt-4">
              However, no method of internet transmission or electronic storage is completely secure. While we strive to protect your data, we cannot guarantee absolute security. You acknowledge that inherent risks exist when transmitting information online.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-surface-100 mb-4">Your Right to Delete Data</h2>
            <p className="text-surface-300 leading-relaxed">
              Upon receiving a verified request, we will delete your personal information from our records and direct our service providers to do the same. However, we may retain information when necessary to:
            </p>
            <ul className="list-disc list-inside text-surface-300 mt-3 space-y-1">
              <li>Complete transactions or fulfill contractual obligations</li>
              <li>Detect, investigate, or prevent fraudulent or illegal activity</li>
              <li>Debug or fix errors affecting Platform functionality</li>
              <li>Exercise legal rights, including free speech protections</li>
              <li>Comply with legal obligations or court orders</li>
              <li>Support legitimate internal uses aligned with your expectations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-surface-100 mb-4">Children's Privacy</h2>
            <p className="text-surface-300 leading-relaxed">
              TradeCraft is not intended for users under the age of thirteen. We do not knowingly collect personal information from children. If you are under thirteen, please do not use this Platform. Users between thirteen and eighteen should obtain parental consent before registering.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-surface-100 mb-4">Email Communications</h2>
            <p className="text-surface-300 leading-relaxed">
              We may periodically send you emails containing announcements, product updates, promotional offers, security alerts, or service-related notifications. You can manage your email preferences or unsubscribe from marketing communications at any time.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-surface-100 mb-4">Data Storage</h2>
            <p className="text-surface-300 leading-relaxed">
              Your information may be stored on servers operated by third-party hosting providers with whom we have contracted. These providers are selected for their security practices and are bound by confidentiality agreements.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-surface-100 mb-4">Policy Updates</h2>
            <p className="text-surface-300 leading-relaxed">
              Colvix LLC reserves the right to modify this Privacy Policy at any time. We will notify you of significant changes by email, by posting a notice on our Platform, or by updating this page. Your continued use of TradeCraft after changes are posted constitutes your acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-surface-100 mb-4">Contact Us</h2>
            <p className="text-surface-300 leading-relaxed">
              If you have questions about this Privacy Policy or believe we have not followed these practices, please contact us:
            </p>
            <div className="mt-4 text-surface-300">
              <p><strong className="text-surface-200">Colvix LLC</strong></p>
              <p className="mt-2">
                Email: <a href="mailto:support@colvix.net" className="text-primary-400 hover:text-primary-300">support@colvix.net</a>
              </p>
            </div>
          </section>
        </div>
      </div>
      </div>
      <SiteFooter />
    </div>
  );
}
